/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import { validateNumber } from "../../../../utils/validateFields";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import style from "../../../../styles/common/formModal.module.css";
import { get } from "../../../../utils/api";
import EditDialog from "../../../../Components/Common/EditDialog";
import MonthYearPicker from "../../../../Components/Common/MonthYearPicker";
import Multiline from "../../../../Components/Masters/Multiline";
import FileField from "../../../../Components/Masters/FileField";
import ShipAutocomplete from "../../../../Components/Common/ShipAutocomplete";
import formatTitleWithOptional from "../../../Scope1/OptionalFeild";
import { useUser } from "../../../../context/AuthContext";

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
}) => {
  const { user } = useUser();

  const initialFormData = {
    consumptionPeriod: {
      month: initialData?.consumptionPeriod?.month || "",
      year: initialData?.consumptionPeriod?.year || "",
    },
    transportationType: initialData?.transportationType || "",
    origin: initialData?.origin || "",
    destination: initialData?.destination || "",
    freightLoad: initialData?.freightLoad
      ? {
          value: initialData?.freightLoad?.value || "",
          unit: initialData?.freightLoad?.unit || "",
        }
      : {},
    distanceCovered: initialData?.distanceCovered
      ? {
          value: initialData?.distanceCovered?.value || "",
          unit: initialData?.distanceCovered?.unit || "",
        }
      : {},
    categoryOfShipment: initialData?.categoryOfShipment || "",
    typeOfShipment: initialData?.typeOfShipment || "",
    remark: initialData?.remark || "",
    documents: initialData?.documents || [],
    editRemarkReason: initialData?.editRemarkReason || "",
  };

  const initialErrors = {};
  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formData2, setFormData2] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedPorts, setSelectedPorts] = useState({
    origin: "",
    destination: "",
  });

  useEffect(() => {
    if (isOpen && isEditing) {
      setFormData(initialData);
      setFormData2(initialData);
      setUploadedFiles(initialData?.documents || []);
    } else {
      setFormData(initialFormData);
      setFormData2(initialFormData);
      setUploadedFiles(
        initialData?.documents !== undefined ? initialData?.documents : []
      );
    }
  }, [isOpen, isEditing, initialData]);

  const handleExitAgree = () => {
    setEditDialog(false);
    onClose();
    setErrors({});
    setFormData(initialFormData);
    setFormData2(initialFormData);
  };

  const handleClose = () => {
    // const isFormDataUnchanged = Object.keys(formData).every(
    //   (key) => formData[key] === formData2[key]
    // );
    const isFormDataUnchanged = Object.keys(formData).every((key) => {
      const value1 = formData[key];
      const value2 = formData2[key];

      if (
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) {
        // For nested objects, do a deep comparison
        return JSON.stringify(value1) === JSON.stringify(value2);
      }

      // For primitive values or arrays, use strict equality
      return value1 === value2;
    });

    if (isFormDataUnchanged) {
      handleExitAgree();
    } else {
      setEditDialog(true);
      // onClose();
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    // Define allowed fields (fields explicitly present in initialFormData)
    const allowedFields = [
      "consumptionPeriod",
      "transportationType",
      "origin",
      "destination",
      "freightLoad",
      "distanceCovered",
      "categoryOfShipment",
      "typeOfShipment",
      "remark",
      "documents",
      // "editRemarkReason",
    ];

    Object.keys(formData).forEach((field) => {
      // Ignore unexpected fields like `createdAt`, `__v`
      if (!allowedFields.includes(field)) return;

      const formatFieldName = (name) =>
        name
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());

      if (
        !["remark", "documents"].includes(field) &&
        (!formData[field] ||
          (typeof formData[field] === "string" && !formData[field].trim()))
      ) {
        newErrors[field] = `${formatFieldName(field)} is required`;
      }

      if (
        ["freightLoad"].includes(field) &&
        (!formData[field]?.value || isNaN(formData[field].value))
      ) {
        newErrors[`${field}Value`] = `Enter numeric value`;
      }

      if (
        ["freightLoad"].includes(field) &&
        typeof formData[field] === "object" &&
        !formData[field].unit
      ) {
        newErrors[`${field}Unit`] = `Unit is required`;
      }

      if (field === "consumptionPeriod") {
        if (!validateNumber(formData[field]?.month))
          newErrors["month"] = "Month is required";
        if (!validateNumber(formData[field]?.year))
          newErrors["year"] = "Year is required";
      }
    });

    // Add validation for Distance Travelled
    if (
      !formData.distanceCovered?.value ||
      formData.distanceCovered.value === ""
    ) {
      newErrors.distanceCoveredValue = "Enter numeric value";
    }

    if (
      !formData.distanceCovered?.unit ||
      formData.distanceCovered.unit === "Tap to Select"
    ) {
      newErrors.distanceCoveredUnit = "Unit is required";
    }
    if (formData?.editRemarkReason === "") {
      if (user?.endUser && isEditing) {
        newErrors.editRemarkReason = "Edit remark is required";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFormData()) {
      return;
    }
    let dataToSend = {
      ...formData,
      documents: uploadedFiles,
    };
    if (user?.endUser && isEditing) {
      dataToSend.requestStatus = "edit";
      dataToSend.requestMode = true;
      dataToSend.approvalStatus = "Pending";
    }
    onSubmit(dataToSend, isEditing);
    onClose();
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (value && value.includes(",")) {
      setSelectedPorts((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (value && value.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: null,
      }));
    }
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
      ...(field === "categoryOfShipment" && { typeOfShipment: "" }),
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: null, // Reset error for the current field
    }));
  };

  const handleNestedChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prevData) => {
      const fieldPath = field.split(".");
      const updatedData = { ...prevData };
      let current = updatedData;

      for (let i = 0; i < fieldPath.length - 1; i++) {
        if (!current[fieldPath[i]]) {
          current[fieldPath[i]] = {};
        }
        current[fieldPath[i]] = { ...current[fieldPath[i]] };
        current = current[fieldPath[i]];
      }

      current[fieldPath[fieldPath.length - 1]] = value;

      return updatedData;
    });
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (field.includes("value")) {
        if (field.includes("distanceCovered")) {
          updatedErrors["distanceCoveredValue"] = null;
        } else if (field.includes("freightLoad")) {
          updatedErrors["freightLoadValue"] = null;
        }
      } else if (field.includes("unit")) {
        if (field.includes("distanceCovered")) {
          updatedErrors["distanceCoveredUnit"] = null;
        } else if (field.includes("freightLoad")) {
          updatedErrors["freightLoadUnit"] = null;
        }
      } else {
        updatedErrors[field] = null;
      }

      return updatedErrors;
    });
  };

  // useEffect(() => {
  //   if (formData.origin && formData.destination) {
  //     fetchDistance(formData.origin, formData.destination);
  //   }
  // }, [formData.origin, formData.destination]);

  // const fetchDistance = async () => {
  //   try {
  //     setLoading(true);
  //     let response = await get(
  //       `/scope3/shipTransportation/distance?origin=${formData.origin}&destination=${formData.destination}`
  //     );
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       distanceCovered: {
  //         value: parseInt(response?.data?.data),
  //         unit: "Km",
  //       },
  //     }));
  //   } catch (error) {
  //     console.error("Error fetching ships:", error);
  //     setLoading(false);
  //   }
  // };

  const getShipmentOptions = () => {
    if (formData?.categoryOfShipment === "Liquid Shipment") {
      return [
        <MenuItem value="Tap to Select" disabled>
          Tap to Select
        </MenuItem>,
        <MenuItem key="Crude tanker" value="Crude tanker">
          Crude tanker
        </MenuItem>,
        <MenuItem key="Products tanker" value="Products tanker">
          Products tanker
        </MenuItem>,
        <MenuItem key="Chemical tanker" value="Chemical tanker">
          Chemical tanker
        </MenuItem>,
        <MenuItem key="LNG tanker" value="LNG tanker">
          LNG tanker
        </MenuItem>,
        <MenuItem key="LPG tanker" value="LPG tanker">
          LPG tanker
        </MenuItem>,
      ];
    } else if (formData?.categoryOfShipment === "Dry Shipment") {
      return [
        <MenuItem value="Tap to Select" disabled>
          Tap to Select
        </MenuItem>,
        <MenuItem key="Bulk carrier" value="Bulk carrier">
          Bulk carrier
        </MenuItem>,
        <MenuItem key="General cargo" value="General cargo">
          General cargo
        </MenuItem>,
        <MenuItem key="Container ship" value="Container ship">
          Container ship
        </MenuItem>,
        <MenuItem key="Vehicle transport" value="Vehicle transport">
          Vehicle transport
        </MenuItem>,
        <MenuItem key="RoRo-Ferry" value="RoRo-Ferry">
          RoRo-Ferry
        </MenuItem>,
        <MenuItem key="Large RoPax Ferry" value="Large RoPax Ferry">
          Large RoPax Ferry
        </MenuItem>,
        <MenuItem key="Refrigerated cargo" value="Refrigerated cargo">
          Refrigerated cargo
        </MenuItem>,
      ];
    }
    return [
      <MenuItem key="default" value="Tap to Select">
        Tap to Select
      </MenuItem>,
    ];
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      // open={addData}
      open={isOpen}
      onClose={handleClose}
      // onClose={handleClose}>
    >
      {/* <Box display="flex" gap={3} sx={{ width: "400px", maxWidth: "100%" }}> */}
      <Box display="flex" gap={3} sx={{ width: "26rem", maxWidth: "100%" }}>
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            // zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box sx={{ width: "88%", p: 3, backgroundColor: "#fff" }}>
          <Box>
            <Typography
              id="modal-title"
              variant="h6"
              sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
            >
              {header}
            </Typography>
            <form>
              <div style={{ marginTop: "1rem 0" }}>
                <MonthYearPicker
                  title={"Consumption Month"}
                  {...{
                    setFormData,
                    formData,
                    errors,
                    setErrors,
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 1 }}
                >
                  Transportation Type
                </Typography>
                <Select
                  size="small"
                  name="transportationType"
                  value={formData?.transportationType || "Tap to Select"}
                  fullWidth
                  onChange={handleChange("transportationType")}
                  error={errors?.transportationType || ""}
                >
                  <MenuItem value="Tap to Select" disabled>
                    Tap to Select
                  </MenuItem>
                  <MenuItem value="Upstream">Upstream Transportation</MenuItem>
                  <MenuItem value="Downstream">
                    Downstream Transportation
                  </MenuItem>
                </Select>
                {errors?.transportationType && (
                  <Typography variant="body2" color="#D34040">
                    {errors?.transportationType}
                  </Typography>
                )}
                {/* <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 1 }}
                >
                  Source
                </Typography> */}
                {/* <TextField
                  type="text"
                  size="small"
                  placeholder="Enter Origin"
                  fullWidth
                  value={formData.origin}
                  onChange={handleChange("origin")}
                  error={!!errors?.origin}
                  InputLabelProps={{
                    style: { color: errors?.origin ? "#D34040" : undefined },
                  }}
                /> */}
                <ShipAutocomplete
                  title={"Source"}
                  // error={errors.origin}
                  value={formData.origin || ""}
                  onChange={(newValue) =>
                    handleLocationChange("origin", newValue)
                  }
                  placeholder="Enter Origin"
                />

                {errors?.origin && (
                  <Typography variant="body2" color="#D34040">
                    {errors?.origin}
                  </Typography>
                )}

                <ShipAutocomplete
                  title={"Destination"}
                  // error={errors.destination}
                  value={formData.destination || ""}
                  onChange={(newValue) =>
                    handleLocationChange("destination", newValue)
                  }
                  placeholder="Enter Destination"
                />
                {errors?.destination && (
                  <Typography variant="body2" color="#D34040">
                    {errors?.destination}
                  </Typography>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: "1rem" }}
                >
                  Shipment Category
                </Typography>
                <Select
                  size="small"
                  name="categoryOfShipment"
                  value={formData?.categoryOfShipment || "Tap to Select"}
                  fullWidth
                  onChange={handleChange("categoryOfShipment")}
                  error={!!errors?.categoryOfShipment}
                >
                  <MenuItem value="Tap to Select" disabled>
                    Tap to Select
                  </MenuItem>
                  <MenuItem value="Liquid Shipment">Liquid Shipment</MenuItem>
                  <MenuItem value="Dry Shipment">Dry Shipment</MenuItem>
                </Select>

                {errors?.categoryOfShipment && (
                  <Typography variant="body2" color="#D34040">
                    {errors?.categoryOfShipment}
                  </Typography>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: "1rem" }}
                >
                  Shipment Type
                </Typography>
                <Select
                  size="small"
                  name="typeOfShipment"
                  fullWidth
                  value={formData?.typeOfShipment || "Tap to Select"}
                  onChange={(event) => {
                    const { value } = event.target;
                    setFormData((prevData) => ({
                      ...prevData,
                      typeOfShipment: value,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      typeOfShipment: null, // Reset error for the current field
                    }));
                  }}
                  disabled={
                    formData?.categoryOfShipment === "" ||
                    formData?.categoryOfShipment === "Tap to Select"
                      ? true
                      : false
                  }
                  error={!!errors?.typeOfShipment}
                >
                  {getShipmentOptions()}
                </Select>

                {errors?.typeOfShipment && (
                  <Typography variant="body2" color="#D34040">
                    {errors?.typeOfShipment}
                  </Typography>
                )}
                <Box marginTop="1rem">
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    Freight Load
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    fullWidth
                  >
                    <Box sx={{ width: "50%" }}>
                      <TextField
                        // type="text"
                        size="small"
                        name="freight"
                        type="number"
                        placeholder="Enter Load"
                        value={formData?.freightLoad?.value}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || parseFloat(value) >= 0) {
                            handleNestedChange("freightLoad.value")(e);
                          }
                        }}
                        error={!!errors?.freightLoadValue}
                        InputLabelProps={{
                          style: {
                            color: errors?.freightLoadValue
                              ? "#D34040"
                              : undefined,
                          },
                        }}
                        InputProps={{
                          inputProps: {
                            inputMode: "numeric", // better mobile keyboard support
                            pattern: "[0-9]*",
                            min: 0,
                          },
                          style: { appearance: "textfield" }, // optional fallback
                        }}
                        sx={{
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield", // Firefox
                          },
                        }}
                      />
                      <Box sx={{ minHeight: "20px" }}>
                        {errors?.freightLoadValue && (
                          <Typography variant="body2" color="#D34040">
                            {errors?.freightLoadValue}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ width: "45%", mb: "1px" }}>
                      <Select
                        size="small"
                        name="freightLoad.unit"
                        value={formData?.freightLoad?.unit || "Tap to Select"}
                        onChange={handleNestedChange("freightLoad.unit")}
                        // sx={{ width: "45%" }}
                        fullWidth
                        error={!!errors?.freightLoadUnit}
                      >
                        <MenuItem value="Tap to Select" disabled>
                          Tap to Select
                        </MenuItem>
                        <MenuItem value="Kg">Kg</MenuItem>
                        <MenuItem value="Tonnes">Tonnes</MenuItem>
                      </Select>
                      <Box sx={{ minHeight: "20px" }}>
                        {errors?.freightLoadUnit && (
                          <Typography variant="body2" color="#D34040">
                            {errors?.freightLoadUnit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    Distance Travelled
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    fullWidth
                  >
                    <Box sx={{ width: "50%" }}>
                      <TextField
                        type="text"
                        size="small"
                        name="distance"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        value={formData.distanceCovered?.value}
                        placeholder="Enter Distance"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || parseFloat(value) >= 0) {
                            handleNestedChange("distanceCovered.value")(e);
                          }
                        }}
                        // sx={{ width: "50%" }}
                        error={!!errors?.distanceCoveredValue}
                        InputLabelProps={{
                          style: {
                            color: errors?.distanceCoveredValue
                              ? "#D34040"
                              : undefined,
                          },
                        }}
                      />
                      <Box sx={{ minHeight: "20px" }}>
                        {errors?.distanceCoveredValue && (
                          <Typography variant="body2" color="#D34040">
                            {errors?.distanceCoveredValue}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ width: "45%", mb: "1px" }}>
                      <Select
                        size="small"
                        name="distanceCovered.unit"
                        // disabled={true}
                        value={
                          formData.distanceCovered?.unit || "Tap to Select"
                        }
                        onChange={handleNestedChange("distanceCovered.unit")}
                        fullWidth
                        error={!!errors?.distanceCoveredUnit}
                      >
                        <MenuItem value="Tap to Select" disabled>
                          Tap to Select
                        </MenuItem>
                        <MenuItem value="Km">Km</MenuItem>
                        <MenuItem value="mile">Mile</MenuItem>
                      </Select>
                      <Box sx={{ minHeight: "20px" }}>
                        {errors?.distanceCoveredUnit && (
                          <Typography variant="body2" color="#D34040">
                            {errors?.distanceCoveredUnit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Multiline
                  // title={"Remark"}
                  title={formatTitleWithOptional("Remark (optional)")}
                  placeholder={"remark"}
                  fieldName="remark"
                  value={formData.remark}
                  handleChange={handleChange("remark")}
                />
                {user?.endUser && isEditing && (
                  <Multiline
                    title={formatTitleWithOptional("Edit Remark Reason")}
                    placeholder="Additional edit remarks"
                    fieldName="editRemarkReason"
                    value={formData.editRemarkReason}
                    handleChange={handleChange("editRemarkReason")}
                    errors={errors.editRemarkReason}
                  />
                )}

                <FileField
                  // title={"Document"}
                  title={formatTitleWithOptional("Upload Documents (optional)")}
                  name={"documents"}
                  errors={errors["documents"]}
                  fileUrls={uploadedFiles}
                  progress={uploadProgress["documents"] || []}
                  {...{ setUploadedFiles, setUploadProgress, setErrors }}
                  // onDelete={handleDeleteFile}
                />
              </div>
            </form>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0a3f73",
                  textTransform: "none",
                  marginTop: "10%",
                }}
                onClick={handleSubmit}
                color="primary"
              >
                Submit
              </Button>
            </Box>
          </Box>
          {/* </Modal> */}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
